import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useInactivityTimeout — a single, shared idle timer for the authenticated app.
 *
 * Design notes that matter for correctness:
 *  - ELAPSED IS COMPUTED FROM A TIMESTAMP, not accumulated by a long setTimeout. A ticking 1s
 *    interval compares `Date.now() - lastActivity`, so a backgrounded/throttled tab, a sleeping
 *    laptop, or a device clock change can't leave a stale timer running — the next tick simply
 *    sees the real elapsed time. On `visibilitychange` we re-check immediately.
 *  - HIGH-FREQUENCY EVENTS (mousemove, scroll, pointermove) only write a ref; no React state and
 *    no cross-tab message fires per event. The cross-tab "I'm active" broadcast is throttled.
 *  - GENUINE ACTIVITY ONLY resets the timer (pointer, key, touch, scroll, click). Background API
 *    calls and timers never touch it.
 *  - SINGLE-FIRE: `firedRef` guarantees onExpire runs once, even across the interval, the
 *    visibility re-check, and a cross-tab logout message arriving together.
 *  - CROSS-TAB: a BroadcastChannel (falling back to a localStorage ping) keeps every tab of the
 *    same browser in sync — activity in one tab resets the others, and a logout in one signs the
 *    others out immediately rather than waiting for their own timers.
 */

export interface InactivityOptions {
  /** When false the timer is fully disarmed (no listeners, no interval). */
  enabled: boolean;
  /** Idle milliseconds before onExpire fires. */
  timeoutMs: number;
  /** Show the warning this many ms before expiry. */
  warnMs: number;
  /** Called once when the idle period elapses (or a sibling tab expired). */
  onExpire: () => void;
}

export interface InactivityState {
  /** True while the pre-logout warning should be shown. */
  warning: boolean;
  /** Milliseconds remaining until logout (only meaningful while `warning`). */
  remainingMs: number;
  /** Reset the timer and dismiss the warning (e.g. "Stay Signed In"). */
  stayActive: () => void;
}

const CHANNEL_NAME = 'creyente-session';
const ACTIVITY_PING_KEY = 'creyente:session:activity-ping';
const LOGOUT_PING_KEY = 'creyente:session:logout-ping';
const BROADCAST_THROTTLE_MS = 3000;

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'pointerdown', 'click'] as const;
const PASSIVE_EVENTS = ['mousemove', 'scroll', 'pointermove', 'wheel', 'touchmove'] as const;

export function useInactivityTimeout(options: InactivityOptions): InactivityState {
  const { enabled, timeoutMs, warnMs, onExpire } = options;

  const lastActivityRef = useRef<number>(Date.now());
  const firedRef = useRef<boolean>(false);
  const lastBroadcastRef = useRef<number>(0);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const [warning, setWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);

  // Broadcast "activity" to sibling tabs (throttled) so they reset too.
  const channelRef = useRef<BroadcastChannel | null>(null);
  const broadcast = useCallback((type: 'activity' | 'logout') => {
    const now = Date.now();
    if (type === 'activity' && now - lastBroadcastRef.current < BROADCAST_THROTTLE_MS) return;
    lastBroadcastRef.current = now;
    try {
      channelRef.current?.postMessage({ type, at: now });
    } catch {
      /* ignore */
    }
    // localStorage fallback for browsers without BroadcastChannel; the value must change to fire.
    try {
      localStorage.setItem(type === 'logout' ? LOGOUT_PING_KEY : ACTIVITY_PING_KEY, String(now));
    } catch {
      /* ignore */
    }
  }, []);

  const markActive = useCallback(
    (localOrigin: boolean) => {
      if (firedRef.current) return;
      lastActivityRef.current = Date.now();
      setWarning((w) => (w ? false : w));
      if (localOrigin) broadcast('activity');
    },
    [broadcast]
  );

  const stayActive = useCallback(() => {
    firedRef.current = false;
    lastActivityRef.current = Date.now();
    setWarning(false);
    broadcast('activity');
  }, [broadcast]);

  const fire = useCallback((localOrigin: boolean) => {
    if (firedRef.current) return;
    firedRef.current = true;
    setWarning(false);
    if (localOrigin) broadcast('logout');
    onExpireRef.current();
  }, [broadcast]);

  useEffect(() => {
    if (!enabled) {
      setWarning(false);
      firedRef.current = false;
      return;
    }

    // Fresh arming: treat mount/enable as activity so the clock starts now.
    lastActivityRef.current = Date.now();
    firedRef.current = false;

    // Cross-tab channel.
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (e: MessageEvent) => {
        const data = e.data as { type?: string } | null;
        if (data?.type === 'activity') markActive(false);
        else if (data?.type === 'logout') fire(false);
      };
    } catch {
      channel = null;
    }
    channelRef.current = channel;

    const onStorage = (e: StorageEvent) => {
      if (e.key === ACTIVITY_PING_KEY) markActive(false);
      else if (e.key === LOGOUT_PING_KEY) fire(false);
    };
    window.addEventListener('storage', onStorage);

    const onActivity = () => markActive(true);
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    PASSIVE_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

    const check = () => {
      if (firedRef.current) return;
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = timeoutMs - elapsed;
      if (remaining <= 0) {
        fire(true);
      } else if (remaining <= warnMs) {
        setWarning(true);
        setRemainingMs(remaining);
      } else {
        setWarning((w) => (w ? false : w));
      }
    };

    const interval = window.setInterval(check, 1000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);
    check();

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      PASSIVE_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      try {
        channel?.close();
      } catch {
        /* ignore */
      }
      channelRef.current = null;
    };
  }, [enabled, timeoutMs, warnMs, markActive, fire]);

  return { warning, remainingMs, stayActive };
}

export default useInactivityTimeout;
